import React from 'react';
import { format, set } from 'date-fns';
import { round } from 'lodash';

import {
  Box,
  CircularProgress,
  SxProps,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';

import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { colors } from '../theme/palette';
import { useFormatNumber } from '../utils/format-number';
import { fontFamily } from '../theme/typography';

import { Warning } from '@mui/icons-material';
import { RouterOutput } from '../api/trpc';
import { useSelector } from '../store/utils';
import { SpotPricePeriod } from '../shared';
import { SpotPriceView } from './use-spot-summary';

type Props = {
  summary:
    | Pick<
        NonNullable<RouterOutput['spot']['getSummary']['summary']>,
        | 'spotPrices'
        | 'spotPrices15M'
        | 'spotPricesWithDistribution'
        | 'spotPrices15MWithDistribution'
      >
    | undefined;
  summaryLoading: boolean;
  summaryError: boolean;
  priceView: SpotPriceView;
  sx?: SxProps;
  hideAxisX?: boolean;
};

// Pick the single value the chart draws for a period, given the active view.
const resolveValue = (
  priceView: SpotPriceView,
  price: number,
  withDistribution: number
) => {
  if (priceView === SpotPriceView.PriceOnly) return price;
  if (priceView === SpotPriceView.DistributionOnly)
    return withDistribution - price;
  return withDistribution;
};

// Domain for the price views; default (zero-based) domain for the distribution
// view so its small, flat values aren't exaggerated. For the price views we
// anchor the baseline at 0 whenever every value is positive — a tight dataMin
// baseline would exaggerate the differences and hide that prices sit well above
// zero — and fall back to dataMin only when there are negative prices.
const getYDomain = (
  priceView: SpotPriceView,
  chartData: { value: number }[] | undefined
): [number | 'dataMin', 'dataMax'] | undefined => {
  if (priceView === SpotPriceView.DistributionOnly) return undefined;

  const min = Math.min(...(chartData?.map((d) => d.value) ?? [0]));
  return [min > 0 ? 0 : 'dataMin', 'dataMax'];
};

export const SpotPricesChart = ({
  summary,
  summaryLoading: isLoading,
  summaryError: isError,
  priceView,
  sx = {},
  hideAxisX = false,
}: Props) => {
  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const pricePeriod = useSelector(
    ({ variables: { SP_PRICE_PERIOD } }) => SP_PRICE_PERIOD
  );

  const is15M = pricePeriod === SpotPricePeriod['15M'];
  const basePrices = is15M ? summary?.spotPrices15M : summary?.spotPrices;
  const distributionPrices = is15M
    ? summary?.spotPrices15MWithDistribution
    : summary?.spotPricesWithDistribution;

  // Single bar per period: the value depends on the active price view (bare
  // spot price, distribution surcharge alone, or their sum).
  const chartData = basePrices?.map((price, period) => {
    const at = is15M
      ? set(new Date(), {
          hours: Math.floor(period / 4),
          minutes: 0 + (period % 4) * 15,
        })
      : set(new Date(), { hours: period, minutes: 0 });

    const withDistribution = distributionPrices?.[period] ?? price;

    return {
      at: format(at, 'HH:mm'),
      value: resolveValue(priceView, price, withDistribution),
    };
  });

  // The distribution surcharge is small and roughly flat; the dataMin–dataMax
  // domain would exaggerate its tiny variations, so fall back to the default
  // (zero-based) domain for that view.
  const yDomain = getYDomain(priceView, chartData);

  return (
    <Box sx={{ p: 0, pt: 1, width: '100%', ...sx }}>
      <Box
        sx={{
          pt: 1,
          pb: 2,
          px: 2,
          height: { xs: 200, sm: 340 },
          width: '100%',
          position: 'relative',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            barCategoryGap={
              pricePeriod === SpotPricePeriod['15M'] ? 0 : sm ? 4 : 1
            }
            barGap={pricePeriod === SpotPricePeriod['15M'] ? 0 : sm ? 4 : 1}
            margin={{ left: 0, right: 0 }}
          >
            <CartesianGrid
              strokeWidth={0.25}
              stroke={colors.darkBlue}
              strokeOpacity={0.4}
              vertical={false}
            />
            <XAxis
              dataKey="at"
              style={{
                fontFamily,
                color: colors.darkBlue,
                top: 20,
                transform: 'translateY(8px)',
              }}
              axisLine={false}
              tickLine={false}
              hide={hideAxisX}
            />
            <YAxis hide domain={yDomain} />
            <Bar dataKey="value" fill={colors.primary} />
          </BarChart>
        </ResponsiveContainer>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: isLoading || isError ? 'flex' : 'none',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {isLoading && (
            <CircularProgress
              size={22}
              sx={{ animationDuration: '850ms', display: 'block' }}
            />
          )}

          {isError && <Warning sx={{ fontSize: 22, color: 'warning.main' }} />}
        </Box>
      </Box>
    </Box>
  );
};

type AxisYProps = Pick<Props, 'summary' | 'priceView'>;

export const SpotPricesChartAxisY = ({ summary, priceView }: AxisYProps) => {
  const pricePeriod = useSelector(
    ({ variables: { SP_PRICE_PERIOD } }) => SP_PRICE_PERIOD
  );

  const formatNumber = useFormatNumber();

  // recharts is plain SVG, so resolve the active scheme to color the labels.
  const { mode, systemMode } = useColorScheme();
  const isDark = (mode === 'system' ? systemMode : mode) === 'dark';
  const axisColor = isDark ? colors.white : colors.darkBlue;

  // Mirror the main chart's stacked data so the axis domain stays in sync.
  const is15M = pricePeriod === SpotPricePeriod['15M'];
  const basePrices = is15M ? summary?.spotPrices15M : summary?.spotPrices;
  const distributionPrices = is15M
    ? summary?.spotPrices15MWithDistribution
    : summary?.spotPricesWithDistribution;

  const chartData = basePrices?.map((price, period) => {
    const at = is15M
      ? set(new Date(), {
          hours: Math.floor(period / 4),
          minutes: 0 + (period % 4) * 15,
        })
      : set(new Date(), { hours: period, minutes: 0 });

    const withDistribution = distributionPrices?.[period] ?? price;

    return {
      at: format(at, 'HH:mm'),
      value: resolveValue(priceView, price, withDistribution),
    };
  });

  return (
    <BarChart
      data={chartData}
      barCategoryGap={0}
      barGap={0}
      margin={{ left: 0, right: 0 }}
      width={70}
      height={340}
    >
      <XAxis dataKey="at" hide />
      <YAxis
        domain={getYDomain(priceView, chartData)}
        style={{ fontFamily, color: axisColor }}
        axisLine={false}
        tickLine={false}
        tickFormatter={(value: number) =>
          formatNumber(round(value, 1), {
            style: 'currency',
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })
        }
        width={70}
        mirror={false}
      />
      <Bar dataKey="value" fill="transparent" />
    </BarChart>
  );
};
